"use client"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { FlagIcon } from "@heroicons/react/24/outline";
import {AlertCircle, Loader2, CheckIcon} from "lucide-react";
import {Alert, AlertDescription, AlertTitle} from "@/components/ui/alert";
import useAnalytics from "@/hooks/useAnalytics";

type FeedbackOption = {
  id: string
  label: string
}

const feedbackOptions: FeedbackOption[] = [
  { id: "inaccurate-score", label: "I think the spin score is inaccurate" },
  { id: "wrong-technique", label: "A manipulation technique is wrong or missing" },
  { id: "misleading-summary", label: "The summary is misleading" },
  { id: "other", label: "Other" },
]

type FeedbackDialogProps = {
  contentId: number
}

export function FeedbackDialog({ contentId }: FeedbackDialogProps) {
  const [selectedOptions, setSelectedOptions] = useState<string[]>([])
  const [comment, setComment] = useState("")
  const [open, setOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [error, setError] = useState<string>('')
  const { track } = useAnalytics();

  const handleOptionChange = (optionId: string) => {
    setSelectedOptions((prev) => (prev.includes(optionId) ? prev.filter((id) => id !== optionId) : [...prev, optionId]))
    setIsSuccess(false)
    setError('')
  }

  const handleSubmit = () => {
    try {
      setIsSubmitting(true)
      fetch("/api/feedback", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          options: selectedOptions,
          comment,
          content_id: contentId,
        }),
      })
        .then((response) => response.json())
        .then((data) => {
          if (!data.error) {
            setIsSubmitting(false)
            setIsSuccess(true)
            resetForm()
          } else {
            throw new Error(data.error)
          }
        })
        .catch((error) => {
          console.error("Error submitting feedback:", error)
          setIsSubmitting(false)
          setError('There was an error submitting your feedback. Please try again later.')
        })
    } catch (error) {
      console.error("Error submitting feedback:", error)
      setIsSubmitting(false)
      setError('There was an error submitting your feedback. Please try again later.')
    }
  }

  const resetForm = () => {
    setSelectedOptions([])
    setComment('')
    setError('')
  }

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen)
    if (!isOpen) {
      resetForm()
      setIsSuccess(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="secondary" onClick={() => track("report_issue", { content_id: contentId })}>
          <FlagIcon className="h-4 w-4" />
          <span className="hidden md:inline">Report Issue</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="w-full">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FlagIcon className="h-5 w-5 text-red-500" />
            Found an issue with this report?
          </DialogTitle>
          <DialogDescription>Help us improve. Let us know what's wrong:</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="space-y-3">
            {feedbackOptions.map((option) => (
              <div key={option.id} className="flex items-center space-x-2">
                <Checkbox
                  id={option.id}
                  checked={selectedOptions.includes(option.id)}
                  onCheckedChange={() => handleOptionChange(option.id)}
                />
                <Label htmlFor={option.id} className="text-sm font-normal">
                  {option.label}
                </Label>
              </div>
            ))}
          </div>
          <div className="space-y-2">
            <Label htmlFor="comments" className="text-sm">
              📝 Additional comments (optional):
            </Label>
            <Textarea
              id="comments"
              placeholder="Please provide more details..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="min-h-[100px]"
            />
          </div>
          {error && (
            <Alert variant="destructive" className="space-x-2">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>
                {error}
              </AlertDescription>
            </Alert>
          )}
          {isSuccess && (
            <Alert variant="success" className="space-x-2">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Your feedback has been submitted!</AlertTitle>
            </Alert>
          )}
        </div>
        <DialogFooter className="flex gap-4">
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
          >
            Cancel
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={selectedOptions.length === 0 || isSubmitting || isSuccess}>
            {isSubmitting && <Loader2 className="animate-spin" />}
            {isSuccess && <CheckIcon className="text-success" />}
            Submit Feedback
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
